import { Link } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import type { Patient, Status } from '@/lib/types';
import { statusLabel } from '@/lib/ui';
import { ChevronRightIcon } from '@/components/ui/Icons';

interface DetailHeaderProps {
  patient: Patient;
  onOpenLatestReport?: () => void;
  reportUrl?: string;
  extraAction?: React.ReactNode;
}

const statusColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

const APPOINTMENT_LABEL: Record<string, string> = {
  programado: 'Hoy 14:30',
  atendida: 'Atendida',
};

export function DetailHeader({ patient: p, reportUrl, extraAction }: DetailHeaderProps) {
  const findingsColor = p.findings === 0 ? '' : statusColor[p.findingsLevel];
  const findingsSub =
    p.findings === 0
      ? 'sin alertas'
      : p.findingsLevel === 'crit'
      ? 'críticos'
      : 'a observar';

  const bioAgeSubSign = p.bioAgeDelta >= 0 ? '+' : '−';
  const bioAgeSub = `${bioAgeSubSign}${Math.abs(p.bioAgeDelta).toFixed(1)} vs cronológica`;

  const apptValue = p.appointmentState ? APPOINTMENT_LABEL[p.appointmentState] ?? '—' : 'Sin agendar';
  const apptSub = p.appointmentState === 'programado'
    ? p.appointmentType ?? 'Próxima cita'
    : p.appointmentState === 'atendida'
    ? 'Última cita'
    : 'Agendar consulta';

  return (
    <div className="glass-panel p-5 px-[22px] mb-4 max-[760px]:p-4">
      <div className="flex items-start gap-4 mb-4 max-[760px]:flex-wrap max-[760px]:gap-3">
        <span className="w-14 h-14 rounded-full bg-soft border border-line text-secondary text-lg font-semibold grid place-items-center shrink-0">
          {p.initials}
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-[22px] font-bold tracking-[-0.022em] m-0 mb-1 leading-[1.2] max-[760px]:text-[19px]">
            {p.name}
          </h1>
          <p className="text-[13px] text-tertiary m-0 mb-1.5">
            {p.age} años · {p.gender} · {p.city}, {p.country}
          </p>
          <div className="text-xs text-muted flex flex-wrap gap-x-3.5 gap-y-1 tnum">
            <span>{p.email}</span>
            <span>{p.phone}</span>
            <span>ID: {p.optId}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 max-[760px]:w-full max-[760px]:justify-center max-[760px]:order-last">
          {extraAction}
          <Link
            className="inline-flex items-center gap-1.5 bg-transparent border border-line-strong text-secondary text-[13px] font-medium px-3.5 py-2 rounded-md cursor-pointer transition-colors hover:bg-soft hover:border-line-default hover:text-primary"
            to={reportUrl ?? '#'}
          >
            Abrir último reporte
            <ChevronRightIcon size={12} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-4 border-t border-line pt-4 max-[760px]:grid-cols-2 max-[760px]:gap-y-3">
        <ScoreFactWithTrend patient={p} first />
        <DetailFact label="Edad biológica" value={p.bioAge.toFixed(1)} sub={bioAgeSub} />
        <DetailFact label="Hallazgos" value={p.findings} valueClass={findingsColor} sub={findingsSub} />
        <DetailFact label="Próxima cita" value={apptValue} sub={apptSub} last />
      </div>
    </div>
  );
}

interface ScoreFactWithTrendProps {
  patient: Patient;
  first?: boolean;
}

function ScoreFactWithTrend({ patient: p, first }: ScoreFactWithTrendProps) {
  // Simple 6-month trend points (mock). In a real app these come from exam history.
  const trendPoints = [62, 64, 65, 64, 63, p.optimalScore];
  const trendMonths = ['Dic', 'Ene', 'Feb', 'Mar', 'Abr', 'May'];

  return (
    <Popover className={`px-4 border-r border-line ${first ? 'pl-0' : ''} max-[760px]:border-r-0 max-[760px]:px-2 max-[760px]:first:pl-0`}>
      <PopoverButton className="block w-full text-left bg-transparent border-0 p-0 cursor-pointer group rounded-sm focus:outline-none">
        <div className="text-[10px] text-muted tracking-[0.04em] uppercase font-semibold mb-1 inline-flex items-center gap-1 group-hover:text-tertiary transition-colors">
          Optimal Score
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 17l6-6 4 4 8-8" />
          </svg>
        </div>
        <div className={`text-lg font-bold tracking-[-0.02em] tnum leading-[1.1] ${statusColor[p.status]}`}>
          {p.optimalScore}
        </div>
        <div className="text-[11px] text-tertiary mt-0.5 font-medium">
          {statusLabel[p.status]} · ver evolución
        </div>
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: 'bottom start', gap: 8 }}
        className="z-[60] w-[320px] bg-surface border border-line rounded-md shadow-elevated p-4 focus:outline-none"
      >
        <header className="flex items-baseline justify-between gap-3 mb-3">
          <h4 className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted m-0">
            Evolución últimos 6 meses
          </h4>
          <span className="text-[11px] text-tertiary tnum">
            {trendPoints[0]} → {p.optimalScore}
          </span>
        </header>
        <MiniTrend points={trendPoints} months={trendMonths} status={p.status} />
      </PopoverPanel>
    </Popover>
  );
}

interface MiniTrendProps {
  points: number[];
  months: string[];
  status: Status;
}

function MiniTrend({ points, months, status }: MiniTrendProps) {
  const w = 280;
  const h = 70;
  const min = Math.min(...points) - 4;
  const max = Math.max(...points) + 4;
  const range = max - min || 1;
  const stepX = w / (points.length - 1);
  const ys = points.map((p) => h - ((p - min) / range) * h);
  const polyline = ys.map((y, i) => `${i * stepX},${y}`).join(' ');
  const lineColor =
    status === 'crit' ? 'var(--c-crit)' : status === 'warn' ? 'var(--c-warn)' : 'var(--c-ok)';

  return (
    <div>
      <svg className="w-full block" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <filter id="miniGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <polyline
          points={polyline}
          fill="none"
          stroke={lineColor}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#miniGlow)"
        />
        {ys.map((y, i) => (
          <circle key={i} cx={i * stepX} cy={y} r="2" fill={lineColor} />
        ))}
      </svg>
      <div className="flex justify-between mt-1.5 text-[10px] text-faint tnum">
        {months.map((m) => (
          <span key={m}>{m}</span>
        ))}
      </div>
    </div>
  );
}

interface DetailFactProps {
  label: string;
  value: string | number;
  valueClass?: string;
  sub: string;
  first?: boolean;
  last?: boolean;
}

function DetailFact({ label, value, valueClass = '', sub, first, last }: DetailFactProps) {
  return (
    <div
      className={`px-4 border-r border-line ${first ? 'pl-0' : ''} ${last ? 'border-r-0 pr-0' : ''} max-[760px]:border-r-0 max-[760px]:px-2 max-[760px]:first:pl-0`}
    >
      <div className="text-[10px] text-muted tracking-[0.04em] uppercase font-semibold mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold tracking-[-0.02em] tnum leading-[1.1] ${valueClass}`}>
        {value}
      </div>
      <div className="text-[11px] text-tertiary mt-0.5 font-medium">{sub}</div>
    </div>
  );
}
